import { Box, Stack } from '@mui/material'
import { useState } from 'react'

import { usePermissions } from '@/features/authorization/contexts/PermissionsContext'
import type { ConfigurationBulkActionProps } from '@/features/controller/components/bulkActions/ConfigurationBulkAction'
import { ConfigurationBulkAction } from '@/features/controller/components/bulkActions/ConfigurationBulkAction'
import type { ConfigurationColumnProps } from '@/features/controller/components/bulkActions/ConfigurationColumn'
import { ConfigurationColumn } from '@/features/controller/components/bulkActions/ConfigurationColumn'
import { ControllerBulkActionSelector } from '@/features/controller/components/bulkActions/ControllerBulkActionSelector'
import { ControllerList } from '@/features/controller/components/bulkActions/ControllerList'
import { ControllerConfigurationFileSetProvider } from '@/features/controller/configuration/contexts/ControllerConfigurationFileSetContext'
import { useReplaceConfigurationBulkAction } from '@/features/controller/hooks/bulkActions/useReplaceConfigurationBulkAction'
import { ControllerRestartProvider } from '@/features/controller/restart/contexts/ControllerRestartContext'
import type { SelectableController, UseControllerBulkAction } from '@/features/controller/types'
import { ControllerBulkAction } from '@/features/controller/types'

const NoSelectedBulkAction: UseControllerBulkAction<object, object> = {
  bulkActionColumnProps: {},
  bulkActionColumnTitle: null,
  bulkActionSectionProps: {},
  controllersListUpdateInProgress: false,
  onBulkActionUpdated: () => {},
}

export function ControllerBulkActionsHostComponent() {
  const { permissions } = usePermissions()
  const canMutateControllers = permissions.has('controllersMutation')
  const [controllers, setControllers] = useState<SelectableController[]>([])
  const [bulkAction, setBulkAction] = useState<ControllerBulkAction | null>(null)

  const configurationBulkAction = useReplaceConfigurationBulkAction()

  const bulkActionImplementations = {
    [ControllerBulkAction.replace_configuration]: configurationBulkAction,
  }

  const selectedBulkAction = bulkAction ? bulkActionImplementations[bulkAction] : NoSelectedBulkAction

  const {
    bulkActionColumnProps,
    bulkActionColumnTitle,
    bulkActionSectionProps,
    controllersListUpdateInProgress,
    onBulkActionUpdated,
  } = selectedBulkAction

  function handleUpdateBulkAction(bulkAction: ControllerBulkAction | null) {
    setBulkAction(bulkAction)
    onBulkActionUpdated(bulkAction)
  }

  const selectedControllers = controllers
    .filter((controller) => controller.isSelected)
    .map((controller) => controller.controller)

  function renderBulkActionColumn({ controller, isSelected }: SelectableController) {
    if (bulkAction === ControllerBulkAction.replace_configuration) {
      return (
        <Box>
          <ControllerConfigurationFileSetProvider controllerId={controller.id!}>
            <ControllerRestartProvider>
              <ConfigurationColumn
                {...(bulkActionColumnProps as Omit<ConfigurationColumnProps, 'controller' | 'isSelected'>)}
                controller={controller}
                isSelected={isSelected}
              />
            </ControllerRestartProvider>
          </ControllerConfigurationFileSetProvider>
        </Box>
      )
    }
    return <Box />
  }

  return (
    <Stack gap={2}>
      <ControllerList
        bulkActionColumnTitle={bulkActionColumnTitle}
        renderBulkActionColumn={renderBulkActionColumn}
        updateInProgress={controllersListUpdateInProgress}
        onControllerListUpdated={setControllers}
      />

      {canMutateControllers && (
        <ControllerBulkActionSelector
          bulkAction={bulkAction}
          selectedControllers={selectedControllers}
          updateBulkAction={handleUpdateBulkAction}
        />
      )}

      {canMutateControllers && bulkAction === ControllerBulkAction.replace_configuration && (
        <ConfigurationBulkAction
          controllers={selectedControllers}
          {...(bulkActionSectionProps as Omit<ConfigurationBulkActionProps, 'controllers'>)}
        />
      )}
    </Stack>
  )
}
